import { http, apiCall } from '@/api/http'
import { resolveDefaultActivationType } from '@/helpers/promo/availableActivationTypes'
import { sleep } from '@/utils'

/** New bundle statuses + legacy single-promo statuses for transition */
export type PromoResolveStatus =
    | 'ok'
    | 'no_project'
    | 'not_found'
    | 'activated'
    | 'available'

export interface PromoPayloadLink {
    path?: string
    keyValue?: boolean
}

export interface PromoResolvePayload {
    integrationId?: string
    durationDays?: number
    title?: string
    needProjectSelector?: boolean
    /** Project root field used to pick a path from `links` (e.g. isDhChatEnabled) */
    toggleKey?: string
    links?: PromoPayloadLink[]
    [key: string]: unknown
}

/** Child promo inside a multi-promo resolve bundle */
export interface PromoBundleItem {
    key: string
    promoId?: string | number
    integrationId?: string | null
    payload?: PromoResolvePayload | null
    activated: boolean
}

export interface PromoResolveResult {
    key: string
    projectId: string | null
    status: PromoResolveStatus
    /** Legacy single-promo payload */
    payload?: PromoResolvePayload | null
    redirectPath: string | null
    /** Multi-promo bundle (one link → several child keys) */
    promos?: PromoBundleItem[] | null
}

export type PromoByKeyStatus = 'ok' | 'not_found'

/** GET api/promo/:key — validate link; needProjectSelector lives in payload */
export interface PromoByKeyResult {
    key: string
    status: PromoByKeyStatus
    payload?: PromoResolvePayload | null
}

export type PromoActivationType = 'integration' | 'discount'

export interface PromoActivateResult {
    alreadyActivated: boolean
    installId: string
}

export type PromoLifecycle = 'offered' | 'activating' | 'activated'
export type PromoRelevance = 'available' | 'already_activated' | 'already_installed'
export type PromoPeriodStatus = 'active' | 'need_prolong' | 'expired'

/** GET api/promo/byProject — source of truth for a project's promos */
export interface PromoByProjectItem {
    promoId?: string | number | null
    key: string
    integrationId?: string | null
    lifecycle?: PromoLifecycle | null
    payload?: PromoResolvePayload | null
    installId?: string | number | null
    activeUntil?: string | null
    subscriptionId?: string | number | null
    periodStatus?: PromoPeriodStatus | null
    activationType?: PromoActivationType | string | null
    /**
     * What the campaign can activate. Does not depend on the project
     * and stays the same at any promo status.
     */
    availableActivationTypes?: PromoActivationType[] | null
    /** Whether to offer this promo now. Computed by the backend. */
    relevance?: PromoRelevance | null
}

export const PROMO_ACTIVATE_ERROR_TOAST =
    'Произошла ошибка при активации подарка. Обратитесь в техподдержку'

const ACTIVATE_MAX_RETRIES = 3
const ACTIVATE_RETRY_INTERVAL_MS = 800

/** GET api/promo/resolve?key=&projectId= — short redirectPath; prefix /project/:id on the client */
export async function resolvePromo(key: string, projectId: string) {
    return apiCall<PromoResolveResult>(() =>
        http.get('promo/resolve', { searchParams: { key, projectId } })
    )
}

/** GET api/promo/:key — validate promo link, no projectId */
export async function getPromoByKey(key: string) {
    return apiCall<PromoByKeyResult>(() => http.get(`promo/${encodeURIComponent(key)}`))
}

/** POST api/promo/activate — до 3 ретраев при ошибке / сети. activationType уходит всегда. */
export async function activatePromo(body: {
    key: string
    projectId: string
    activationType?: PromoActivationType
    /** Used only when activationType is omitted. Not sent to the API. */
    availableActivationTypes?: PromoActivationType[] | null
}) {
    const payload = {
        key: body.key,
        projectId: body.projectId,
        activationType:
            body.activationType || resolveDefaultActivationType(body.availableActivationTypes),
    }
    let lastError: unknown = null
    for (let attempt = 0; attempt <= ACTIVATE_MAX_RETRIES; attempt++) {
        try {
            const result = await apiCall<PromoActivateResult>(() =>
                http.post('promo/activate', { json: payload })
            )
            if (result.data && !result.error) {
                return result
            }
            lastError = result.error
        } catch (error) {
            lastError = error
        }
        if (attempt < ACTIVATE_MAX_RETRIES) {
            await sleep(ACTIVATE_RETRY_INTERVAL_MS)
        }
    }
    return { data: null, error: lastError || PROMO_ACTIVATE_ERROR_TOAST }
}

/** GET api/promo/byProject?projectId= — offered + activating + activated */
export async function getPromosByProject(projectId: string) {
    return apiCall<PromoByProjectItem[] | null>(() =>
        http.get('promo/byProject', { searchParams: { projectId } })
    )
}

export function daysLeftUntil(activeUntil: string | Date | null | undefined): number | null {
    if (!activeUntil) return null
    const end = new Date(activeUntil).getTime()
    if (Number.isNaN(end)) return null
    return Math.max(0, Math.ceil((end - Date.now()) / 86_400_000))
}
